/**
 * Helpers for server-side table sorting. The API contract is `?sortBy=<key>&sortOrder=asc|desc`;
 * a column's `sortKey` (when set) is the API key, otherwise its grid `field` is used.
 */

/** DataGrid sort model -> `{ sortBy, sortOrder }`. An empty model returns `{}` (server default order). */
export const toSortParams = (sortModel, columns = []) => {
  const { field, sort } = sortModel?.[0] ?? {};

  if (!field || !sort) return {};

  const column = columns.find((item) => item.field === field);

  return { sortBy: column?.sortKey ?? field, sortOrder: sort };
};

/** `{ sortBy, sortOrder }` -> DataGrid sort model; the inverse of toSortParams. */
export const toSortModel = (params, columns = []) => {
  const { sortBy, sortOrder } = params ?? {};

  if (!sortBy || !sortOrder) return [];

  const column = columns.find((item) => (item.sortKey ?? item.field) === sortBy);

  return [{ field: column?.field ?? sortBy, sort: sortOrder }];
};

/** Adds `sortBy` / `sortOrder` from a fetch payload to a request query, only when both are set. */
export const appendSortParams = (query, { sortBy, sortOrder } = {}) => {
  if (sortBy && sortOrder) {
    query.sortBy = sortBy;
    query.sortOrder = sortOrder;
  }

  return query;
};

const SORT_ORDERS = ['asc', 'desc'];

/**
 * Reads `sortBy` / `sortOrder` from URL search params. Values not in `allowedKeys` (or an invalid
 * order) are ignored, so a hand-edited URL can never send a request the API would reject.
 */
export const parseSortSearchParams = (searchParams, allowedKeys = []) => {
  const sortBy = searchParams.get('sortBy');
  const sortOrder = searchParams.get('sortOrder');

  if (!allowedKeys.includes(sortBy) || !SORT_ORDERS.includes(sortOrder)) return {};

  return { sortBy, sortOrder };
};

/** `{ sortBy, sortOrder }` -> URL query params; empty when there is no sort (keeps the URL clean). */
export const buildSortQueryParams = ({ sortBy, sortOrder } = {}) =>
  sortBy && sortOrder ? { sortBy, sortOrder } : {};
